import { formatDollarsLong } from './formatDollarsLong';
import { getNumberFormat } from './getNumberFormat';

const defaultResult = '$0.00';

const currencyFormatter = getNumberFormat('en', {
    style: 'currency',
    currency: 'USD',
});

/**
 * @typedef {FormatDollarsInput}
 * @property {number} value the number to be formatted into a string
 * @property {FormatDollarsRange[]} ranges an array of formatting range objects
 */
interface FormatDollarsInput {
    value: number;
    ranges: FormatDollarsRange[];
}

/**
 * Formats a number as a string with dollar sign, thousands separators, and a given level of
 * precision, following a specific set of ranges. To ensure that all possible values are covered,
 * make sure you include a final range with a `max` set to `Infinity`.
 * @param {FormatDollarsInput} input
 * @returns {string} the value formatted as a dollar amount
 */
export const formatDollarsByRange = ({
    value,
    ranges,
}: FormatDollarsInput): string => {
    let targetRange = ranges[0];

    const absoluteValue = Math.abs(value);

    ranges.forEach((range: FormatDollarsRange, index: number): void => {
        const { max } = range;
        const priorMax = ranges?.[index - 1]?.max ?? -Infinity;

        if (absoluteValue > priorMax && absoluteValue <= max) {
            targetRange = range;
        }
    });

    if (targetRange === undefined) {
        return defaultResult;
    }

    if (targetRange.style === 'compact') {
        const formatter = getNumberFormat('en', {
            notation: 'compact',
            compactDisplay: 'short',
            maximumFractionDigits: targetRange.precision,
        });

        return `$${formatter.format(value)}`;
    }

    if (targetRange.precision < 3) {
        return currencyFormatter.format(value);
    }

    const formattedAbsoluteValue = formatDollarsLong(
        absoluteValue,
        targetRange.precision,
    );

    if (value < 0) {
        return `-${formattedAbsoluteValue}`;
    }

    return formattedAbsoluteValue;
};
